// Shared location/screen display helper — the single source of truth for
// turning a device's live `location` string (from
// deviceManagement/{familyId}/status/accountVisible.location) into a display
// { name, type }. Used by both the Health tab and the Device tab so the two
// always show the same value.
//
// `screen` is one of: 'home', 'training', 'sleep', 'loading', or an iglooID
// (optionally suffixed with '-<position>', e.g. "abc123-centre"). When the
// device is offline we deliberately don't show stale location data.
//
// `iglooNames` maps iglooId -> friendly name (see useIglooNames); it defaults
// to {} so callers without the map still get a sensible iglooId fallback.
// Returns the igloo ID for a location string, or null when `screen` is not an
// igloo (empty / home / training / sleep / loading). Strips any "-<position>"
// suffix (e.g. "abc123-centre" -> "abc123"). Exported so call sites can compute
// which igloo names need resolving without duplicating the parsing rule.
export function extractIglooId(screen) {
  if (!screen) return null;
  if (screen === 'home' || screen === 'training' || screen === 'sleep' || screen === 'loading') {
    return null;
  }
  return screen.includes('-') ? screen.split('-')[0] : screen;
}

export function getLocationDisplay(screen, isOnline, iglooNames = {}) {
  // If not online, don't show stale location data
  if (!isOnline) {
    return { name: '-', type: 'offline' };
  }

  if (!screen) return { name: 'Unknown', type: 'unknown' };
  if (screen === 'home') return { name: 'Home', type: 'home' };
  if (screen === 'training') return { name: 'Training', type: 'training' };
  if (screen === 'sleep') return { name: 'Sleep', type: 'sleep' };
  if (screen === 'loading') return { name: 'Loading', type: 'loading' };

  // It's an igloo ID (possibly with position like "iglooId-centre")
  const iglooId = extractIglooId(screen);
  const position = screen.includes('-') ? screen.split('-')[1] : null;
  const iglooName = iglooNames[iglooId] || iglooId;

  return {
    name: iglooName,
    type: 'igloo',
    iglooId: iglooId,
    position: position,
  };
}
